import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Clock, User, Building2, Eye, X } from 'lucide-react';
import { 
  interventions as interventionsData,
  centres,
  getTechniciens,
  getTypeInterventionById,
  getCentreById,
  getUserById 
} from '../../data/mockData';
import { format, addDays, startOfWeek, addWeeks, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

// Couleurs pour les centres
const centreColors = [
  '#3b82f6', // bleu
  '#10b981', // vert
  '#f59e0b', // orange
  '#8b5cf6', // violet
  '#ef4444', // rouge
  '#06b6d4', // cyan
  '#ec4899', // rose
];

function AgendaGlobal({ user }) {
  const [searchParams] = useSearchParams();
  const techniciens = getTechniciens();
  const [selectedTechniciens, setSelectedTechniciens] = useState(
    techniciens.map(t => t.id)
  );
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('semaine');
  const [selectedCentre, setSelectedCentre] = useState('');
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const isReadOnly = user.role === 'technicien';

  // Récupérer les paramètres URL (pour l'accès rapide du dashboard)
  useEffect(() => {
    const centreParam = searchParams.get('centre');
    const dateParam = searchParams.get('date');
    
    if (centreParam) {
      setSelectedCentre(centreParam);
    }
    if (dateParam) {
      setCurrentDate(parseISO(dateParam));
      setViewMode('jour');
    }
  }, [searchParams]);

  const startDate = viewMode === 'semaine' 
    ? startOfWeek(currentDate, { locale: fr, weekStartsOn: 1 }) 
    : currentDate;

  const getDaysToShow = () => {
    if (viewMode === 'jour') return [currentDate];
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(startDate, i));
    }
    return days;
  };

  const days = getDaysToShow();

  const navigatePrevious = () => {
    if (viewMode === 'jour') {
      setCurrentDate(addDays(currentDate, -1));
    } else {
      setCurrentDate(addWeeks(currentDate, -1));
    }
  };

  const navigateNext = () => {
    if (viewMode === 'jour') {
      setCurrentDate(addDays(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
  };

  const toggleTechnicien = (technicienId) => {
    setSelectedTechniciens(prev => 
      prev.includes(technicienId)
        ? prev.filter(id => id !== technicienId)
        : [...prev, technicienId]
    );
  };

  const toggleAllTechniciens = () => {
    if (selectedTechniciens.length === techniciens.length) {
      setSelectedTechniciens([]);
    } else {
      setSelectedTechniciens(techniciens.map(t => t.id));
    }
  };

  const getInterventionsForDay = (date, technicienId) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    let result = interventionsData.filter(i => 
      i.technicienId === technicienId && 
      i.datePrevu === dateStr
    );
    
    // Filtrer par centre si sélectionné
    if (selectedCentre) {
      result = result.filter(i => i.centreId === parseInt(selectedCentre));
    }
    
    return result;
  };

  const getCentreColor = (centreId) => {
    const index = centres.findIndex(c => c.id === centreId);
    return centreColors[index % centreColors.length];
  };

  const getDurationStatus = (intervention) => {
    if (!intervention.dureeReelle) return 'pending';
    
    const type = getTypeInterventionById(intervention.typeInterventionId);
    if (!type || !type.dureeEstimee) return 'no-limit';
    
    return intervention.dureeReelle <= type.dureeEstimee ? 'on-time' : 'over-time';
  };

  const handleViewIntervention = (intervention) => {
    setSelectedIntervention(intervention);
  };

  const closeModal = () => {
    setSelectedIntervention(null);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>{isReadOnly ? 'Agenda' : 'Agenda Global'}</h1>
          <p className="text-muted">
            {isReadOnly ? 'Consultation des interventions' : 'Vue d\'ensemble des interventions'}
          </p>
        </div>
      </div>

      {/* Filtre par centre */}
      <div className="centre-filter-chips">
        <button
          className={`centre-chip ${!selectedCentre ? 'active' : ''}`}
          onClick={() => setSelectedCentre('')}
        >
          Tous les centres
        </button>
        {centres.filter(c => c.actif).map((centre, index) => (
          <button
            key={centre.id}
            className={`centre-chip ${selectedCentre === String(centre.id) ? 'active' : ''}`}
            onClick={() => setSelectedCentre(selectedCentre === String(centre.id) ? '' : String(centre.id))}
            style={{ 
              borderColor: centreColors[index % centreColors.length],
              ...(selectedCentre === String(centre.id) && { 
                backgroundColor: centreColors[index % centreColors.length],
                borderColor: centreColors[index % centreColors.length]
              })
            }}
          >
            <span 
              className="centre-color-indicator"
              style={{ backgroundColor: centreColors[index % centreColors.length] }}
            ></span>
            {centre.nom}
          </button>
        ))}
      </div>

      <div className="agenda-controls">
        <div className="view-switcher">
          <button 
            className={`btn ${viewMode === 'jour' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('jour')}
          >
            Jour
          </button>
          <button 
            className={`btn ${viewMode === 'semaine' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('semaine')}
          >
            Semaine
          </button>
        </div>

        <div className="date-navigation">
          <button onClick={navigatePrevious} className="btn btn-secondary">
            <ChevronLeft size={20} />
          </button>
          <button onClick={navigateToday} className="btn btn-secondary">
            Aujourd'hui
          </button>
          <button onClick={navigateNext} className="btn btn-secondary">
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="current-period">
          {viewMode === 'jour' ? (
            <h2>{format(currentDate, 'EEEE d MMMM yyyy', { locale: fr })}</h2>
          ) : (
            <h2>
              Semaine du {format(startDate, 'd MMM', { locale: fr })} au {format(addDays(startDate, 6), 'd MMM yyyy', { locale: fr })}
            </h2>
          )}
        </div>
      </div>

      <div className="techniciens-selector">
        <button onClick={toggleAllTechniciens} className="btn btn-sm btn-secondary">
          {selectedTechniciens.length === techniciens.length ? 'Tout désélectionner' : 'Tout sélectionner'}
        </button>
        
        <div className="techniciens-list">
          {techniciens.map(tech => (
            <button
              key={tech.id}
              onClick={() => toggleTechnicien(tech.id)}
              className={`tech-filter-btn ${selectedTechniciens.includes(tech.id) ? 'active' : ''}`}
            >
              <User size={16} />
              {tech.prenom} {tech.nom}
            </button>
          ))}
        </div>
      </div>

      <div className="agenda-global-container">
        {selectedTechniciens.length === 0 ? (
          <div className="empty-state">
            <User size={48} className="icon-muted" />
            <p>Sélectionnez au moins un technicien</p>
          </div>
        ) : (
          selectedTechniciens.map(techId => {
            const technicien = getUserById(techId);
            const centre = getCentreById(technicien.centreId);
            
            return (
              <div key={techId} className="technicien-agenda">
                <div className="technicien-header">
                  <div className="technicien-info">
                    <div className="user-avatar user-avatar-sm">
                      {technicien.prenom[0]}{technicien.nom[0]}
                    </div>
                    <div>
                      <h3>{technicien.prenom} {technicien.nom}</h3>
                      <p className="text-sm text-muted">{centre?.nom}</p>
                    </div>
                  </div>
                </div>

                <div className="technicien-days">
                  {days.map(day => {
                    const interventions = getInterventionsForDay(day, techId);
                    const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                    
                    return (
                      <div key={day.toISOString()} className={`tech-day ${isToday ? 'today' : ''}`}>
                        <div className="tech-day-header">
                          <p className="text-sm">{format(day, 'EEE d', { locale: fr })}</p>
                          {interventions.length > 0 && (
                            <span className="badge badge-sm badge-primary">{interventions.length}</span>
                          )}
                        </div>

                        <div className="tech-day-interventions">
                          {interventions.length === 0 ? (
                            <div className="tech-empty-day">-</div>
                          ) : (
                            interventions
                              .sort((a, b) => a.heurePrevu.localeCompare(b.heurePrevu))
                              .map(intervention => {
                                const type = getTypeInterventionById(intervention.typeInterventionId);
                                const centre = getCentreById(intervention.centreId);
                                const durationStatus = getDurationStatus(intervention);
                                
                                return (
                                  <div 
                                    key={intervention.id} 
                                    className="tech-intervention clickable"
                                    style={{ 
                                      borderLeftColor: getCentreColor(intervention.centreId),
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => handleViewIntervention(intervention)}
                                  >
                                    <div className="tech-intervention-header">
                                      <div className="tech-intervention-time">
                                        {intervention.heurePrevu}
                                      </div>
                                      {intervention.statut === 'terminee' && (
                                        <span className={`intervention-duration-indicator ${durationStatus}`}>
                                          {durationStatus === 'over-time' && '⚠️'}
                                          {durationStatus === 'on-time' && '✓'}
                                          {intervention.dureeReelle}min
                                        </span>
                                      )}
                                    </div>
                                    {type && (
                                      <span 
                                        className="type-badge type-badge-xs" 
                                        style={{ backgroundColor: type.couleur }}
                                      >
                                        {type.code}
                                      </span>
                                    )}
                                    <p className="tech-intervention-plaque">{intervention.plaque}</p>
                                  </div>
                                );
                              })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal de détails de l'intervention */}
      {selectedIntervention && (
        <>
          <div className="modal-overlay" onClick={closeModal}></div>
          <div className="modal">
            <div className="modal-header">
              <h2>Détails de l'intervention</h2>
              <button onClick={closeModal} className="modal-close">×</button>
            </div>
            
            <div className="modal-body">
              {(() => {
                const technicien = getUserById(selectedIntervention.technicienId);
                const centre = getCentreById(selectedIntervention.centreId);
                const type = getTypeInterventionById(selectedIntervention.typeInterventionId);
                const durationStatus = getDurationStatus(selectedIntervention);
                
                return (
                  <>
                    <div className="modal-section">
                      <h3>Informations générales</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <p className="text-muted">Plaque / Châssis</p>
                          <p><strong>{selectedIntervention.plaque}</strong></p>
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Type</p>
                          {type && (
                            <span 
                              className="type-badge" 
                              style={{ backgroundColor: type.couleur }}
                            >
                              {type.code} - {type.libelle}
                            </span>
                          )}
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Technicien</p>
                          <p>{technicien?.prenom} {technicien?.nom}</p>
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Centre</p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span 
                              className="centre-color-indicator"
                              style={{ backgroundColor: getCentreColor(selectedIntervention.centreId) }}
                            ></span>
                            <p style={{ margin: 0 }}>{centre?.nom}</p>
                          </div>
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Date prévue</p>
                          <p>{format(new Date(selectedIntervention.datePrevu), 'EEEE d MMMM yyyy', { locale: fr })}</p>
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Heure prévue</p>
                          <p>{selectedIntervention.heurePrevu}</p>
                        </div>
                      </div>
                    </div>

                    {selectedIntervention.statut === 'terminee' && (
                      <div className="modal-section">
                        <h3>Réalisation</h3>
                        <div className="info-grid">
                          <div className="info-item">
                            <p className="text-muted">Début réel</p>
                            <p>{selectedIntervention.heureDebut}</p>
                          </div>
                          <div className="info-item">
                            <p className="text-muted">Fin réelle</p>
                            <p>{selectedIntervention.heureFin}</p>
                          </div>
                          <div className="info-item">
                            <p className="text-muted">Durée estimée</p>
                            <p>{type?.dureeEstimee ? `${type.dureeEstimee} min` : 'Non définie'}</p>
                          </div>
                          <div className="info-item">
                            <p className="text-muted">Durée réelle</p>
                            <p>
                              <span className={`intervention-duration-indicator ${durationStatus}`}>
                                {durationStatus === 'over-time' && '⚠️ '}
                                {durationStatus === 'on-time' && '✓ '}
                                {selectedIntervention.dureeReelle} min
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedIntervention.statut === 'en_cours' && (
                      <div className="modal-section">
                        <div className="alert alert-warning" style={{ margin: 0 }}>
                          <Clock size={20} />
                          Intervention en cours depuis {selectedIntervention.heureDebut}
                        </div>
                      </div>
                    )}

                    {selectedIntervention.commentaire && (
                      <div className="modal-section">
                        <h3>Commentaire</h3>
                        <p>{selectedIntervention.commentaire}</p>
                      </div>
                    )}

                    {(selectedIntervention.photoAvant || selectedIntervention.photoApres) && (
                      <div className="modal-section">
                        <h3>Photos</h3>
                        <div className="photos-grid">
                          {selectedIntervention.photoAvant && (
                            <div className="photo-item">
                              <p className="text-sm text-muted">Avant</p>
                              <img 
                                src={selectedIntervention.photoAvant} 
                                alt="Photo avant" 
                                className="modal-image"
                              />
                            </div>
                          )}
                          {selectedIntervention.photoApres && (
                            <div className="photo-item">
                              <p className="text-sm text-muted">Après</p>
                              <img 
                                src={selectedIntervention.photoApres} 
                                alt="Photo après" 
                                className="modal-image"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            
            <div className="modal-footer">
              <button onClick={closeModal} className="btn btn-secondary">
                Fermer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AgendaGlobal;

